import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  Divider,
  Stack,
  Collapse,
  Alert,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  AttachFile as AttachFileIcon,
  SaveAlt as SaveIcon,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { openEmailModal } from "../../store/adminSlice";
import { apiClient } from "../../utils/apiClient";
import { PROPERTY_NAMES } from "../../../shared/shipments/shipmentPropertiesUtils";
import { DatePropertyNames } from "../../../shared/shipments/utils/shipmentDateFormatting";
import { GptRequestsAccordion } from "../emailAutomationPipeline/components/GptRequestsAccordion";
import { DiagnosticEventsDisplay } from "../emailAutomationPipeline/components/DiagnosticEventsDisplay";
import { ShipmentTimelineRenderer } from "../../../shared/shipments/timelineWithThreadLinesComponent/ShipmentTimelineRenderer";
import { useShipmentData } from "../../../shared/shipments/timelineWithThreadLinesComponent/hooks";
import { downloadAttachment } from "../../../shared/shipments/timelineWithThreadLinesComponent/utils";
import { ShipmentDetails } from "../../../shared/shipments/ShipmentDetails";
import type { GetShipmentByIdResponse } from "@services/shipments/types";
import { AdminWorkflowActionTracker } from "../adminCommandsComponents/AdminWorkflowActionTracker";

const ALL_REFERENCE_NAMES = PROPERTY_NAMES;
const DATE_PROPERTY_NAMES = DatePropertyNames;

interface AvailableConfig {
  id: string;
  name: string;
  description: string | null;
}

interface SimulationDefaults {
  emailAutomationPipelineConfigId: string | null;
  activeReminders: string[];
  enabledActionReminders: string[];
  allReminderTypes: Array<{ type: string; name: string; category: string }>;
  availableConfigs: AvailableConfig[];
}

interface SimulationResult {
  timeline: Array<{
    emailId: string;
    subject: string;
    categories: string[];
    timestamp: string;
    from: string;
  }>;
  reminders: {
    newReminders: Array<{
      type: string;
      name: string;
      description?: string;
      dueTime: string;
      triggerTime: string;
      reason: { description: string; shipmentEmailId?: string };
    }>;
    resolvedReminders: Array<{
      reminderId: string;
      reason: { description: string; shipmentEmailId?: string };
    }>;
    analysisStats: {
      emailAnalyzedCount: number;
      updateAnalyzedCount: number;
      totalEventsCount: number;
    };
    diagnosticEvents?: Array<{ timestamp: string; level: string; message: string }>;
  };
  actions: Array<{
    forReminderType: string;
    forReminderName: string;
    actionType: string;
    data: Record<string, unknown>;
    gptRequestId: string;
    diagnosticEvents: Array<{ timestamp: string; level: string; message: string }>;
    resolvedReplyToEmailId?: string;
    resolvedReplyToSubject?: string;
    resolvedAttachments?: Array<{ attachmentId: string; filename: string; emailId: string }>;
  }>;
  shipmentProperties: Array<{ name: string; value: string }>;
}

interface PropertyOverride {
  name: string;
  value: string;
}

interface SimulateRemindersDialogProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
}

export function SimulateRemindersDialog({
  open,
  onClose,
  shipmentId,
}: SimulateRemindersDialogProps) {
  const dispatch = useDispatch();

  // Shipment events and details for timeline
  const { events: allEvents, shipment, loading: eventsLoading, refetch } = useShipmentData(
    shipmentId,
    false,
  );

  // Defaults
  const [defaults, setDefaults] = useState<SimulationDefaults | null>(null);
  const [loadingDefaults, setLoadingDefaults] = useState(true);

  // Controls
  const [configId, setConfigId] = useState("");
  const [cutOffDate, setCutOffDate] = useState("");

  // Filter events by cutoff date for the timeline
  const timelineEvents = useMemo(() => {
    if (!cutOffDate) return allEvents;
    const cutoff = new Date(cutOffDate).getTime();
    return allEvents.filter((e) => {
      if (!e.date) return true;
      return new Date(e.date).getTime() <= cutoff;
    });
  }, [allEvents, cutOffDate]);
  const [runRemindersAtTime, setRunRemindersAtTime] = useState(
    toLocalDateTimeString(new Date()),
  );
  const [propertyOverrides, setPropertyOverrides] = useState<PropertyOverride[]>([]);
  const [activeReminders, setActiveReminders] = useState<string[]>([]);
  const [enabledActionReminders, setEnabledActionReminders] = useState<string[]>([]);

  // Email selection state (excluded emails)
  const [excludedEmailIds, setExcludedEmailIds] = useState<Set<string>>(new Set());

  const allEmailIds = useMemo(() => {
    const ids: string[] = [];
    for (const e of timelineEvents) {
      if (e.emailId) ids.push(e.emailId);
    }
    return ids;
  }, [timelineEvents]);

  const selectedEmailIds = useMemo(() => {
    const selected = new Set<string>();
    for (const id of allEmailIds) {
      if (!excludedEmailIds.has(id)) selected.add(id);
    }
    return selected;
  }, [allEmailIds, excludedEmailIds]);

  const handleToggleEmail = useCallback((emailId: string) => {
    setExcludedEmailIds((prev) => {
      const next = new Set(prev);
      if (next.has(emailId)) {
        next.delete(emailId);
      } else {
        next.add(emailId);
      }
      return next;
    });
  }, []);

  const handleStyleMessage = useCallback(
    (emailId: string): React.CSSProperties | undefined => {
      if (excludedEmailIds.has(emailId)) return { opacity: 0.4 };
      return undefined;
    },
    [excludedEmailIds],
  );

  // Shipment details collapse state
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  // Apply property overrides to shipment for display
  const shipmentWithOverrides = useMemo((): GetShipmentByIdResponse | null => {
    if (!shipment) return null;
    const validOverrides = propertyOverrides.filter((p) => p.name && p.value);
    if (validOverrides.length === 0) return shipment;

    const overrideMap = new Map(validOverrides.map((o) => [o.name, o.value]));
    // Replace existing properties or add new ones
    const updatedProperties = shipment.properties.map((prop) => {
      const override = overrideMap.get(prop.name);
      if (override !== undefined) {
        overrideMap.delete(prop.name); // consumed
        return { ...prop, value: override };
      }
      return prop;
    });
    // Add overrides for properties not already present
    overrideMap.forEach((value, name) => {
      updatedProperties.push({ name: name as any, value });
    });
    return { ...shipment, properties: updatedProperties };
  }, [shipment, propertyOverrides]);

  // Build ShipmentUpdate[] to highlight overridden properties in the details
  const overrideUpdates = useMemo(() => {
    if (!shipment) return [];
    const validOverrides = propertyOverrides.filter((p) => p.name && p.value);
    if (validOverrides.length === 0) return [];

    const existingProps = new Map<string, string>();
    shipment.properties.forEach((p) => {
      if (!existingProps.has(p.name)) existingProps.set(p.name, p.value);
    });

    return validOverrides.map((o) => {
      const oldValue = existingProps.get(o.name);
      return {
        id: `override-${o.name}`,
        updateType: oldValue ? "modifiedProperty" as const : "addedProperty" as const,
        propertyName: o.name,
        newValue: o.value,
        oldValue: oldValue ?? null,
        status: "approved" as const,
      };
    });
  }, [shipment, propertyOverrides]);

  // Results
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persist state
  const [persisting, setPersisting] = useState(false);
  const [persistResult, setPersistResult] = useState<{
    createdReminderIds: string[];
    createdActionIds: string[];
    pipelineResultId: string | null;
    errors: string[];
  } | null>(null);
  const [showPersistConfirm, setShowPersistConfirm] = useState(false);

  // Fetch defaults on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchDefaults = async () => {
      setLoadingDefaults(true);
      try {
        const data = await apiClient.get<SimulationDefaults>(
          "/admin/monitoring/simulation-defaults",
        );
        if (cancelled) return;
        setDefaults(data);
        setConfigId(data.emailAutomationPipelineConfigId || "");
        setActiveReminders(data.activeReminders);
        setEnabledActionReminders(data.enabledActionReminders);
      } catch (err) {
        console.error("Failed to fetch simulation defaults:", err);
      } finally {
        if (!cancelled) setLoadingDefaults(false);
      }
    };

    fetchDefaults();
    return () => { cancelled = true; };
  }, [open]);

  const handleSimulate = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setPersistResult(null);
    setShowPersistConfirm(false);

    try {
      const body: Record<string, unknown> = {
        shipmentId,
        runRemindersAtTime: new Date(runRemindersAtTime).toISOString(),
        activeReminders,
        enabledActionReminders,
      };
      if (cutOffDate) {
        body.cutOffEventsAfterDate = new Date(cutOffDate).toISOString();
      }
      if (propertyOverrides.length > 0) {
        body.propertyOverrides = propertyOverrides.filter(
          (p) => p.name && p.value,
        );
      }
      if (configId) {
        body.emailAutomationPipelineConfigId = configId;
      }
      if (excludedEmailIds.size > 0) {
        body.excludeEmailIds = Array.from(excludedEmailIds);
      }

      const data = await apiClient.post<SimulationResult>(
        "/admin/monitoring/simulate-reminders-and-actions",
        body,
      );
      setResult(data);
    } catch (err: any) {
      setError(err?.message || "Simulation failed");
    } finally {
      setLoading(false);
    }
  }, [
    shipmentId,
    runRemindersAtTime,
    cutOffDate,
    propertyOverrides,
    configId,
    activeReminders,
    enabledActionReminders,
    excludedEmailIds,
  ]);

  const handlePersist = useCallback(async () => {
    if (!result) return;
    setPersisting(true);
    setPersistResult(null);

    try {
      const body: Record<string, unknown> = {
        shipmentId,
        runRemindersAtTime: new Date(runRemindersAtTime).toISOString(),
        activeReminders,
        emailAutomationPipelineConfigId: configId,
        simulatedActions: result.actions
          .filter((a) => a.actionType !== "error")
          .map((a) => ({
            actionType: a.actionType,
            data: a.data,
            forReminderType: a.forReminderType,
            gptRequestId: a.gptRequestId,
          })),
      };
      if (cutOffDate) {
        body.cutOffEventsAfterDate = new Date(cutOffDate).toISOString();
      }
      if (propertyOverrides.length > 0) {
        body.propertyOverrides = propertyOverrides.filter(
          (p) => p.name && p.value,
        );
      }
      if (excludedEmailIds.size > 0) {
        body.excludeEmailIds = Array.from(excludedEmailIds);
      }

      const data = await apiClient.post<{
        createdReminderIds: string[];
        createdActionIds: string[];
        pipelineResultId: string | null;
        errors: string[];
      }>("/admin/monitoring/persist-simulation-results", body);
      setPersistResult(data);
      setShowPersistConfirm(false);
    } catch (err: any) {
      setPersistResult({
        createdReminderIds: [],
        createdActionIds: [],
        pipelineResultId: null,
        errors: [err?.message || "Persist failed"],
      });
    } finally {
      setPersisting(false);
    }
  }, [
    result,
    shipmentId,
    runRemindersAtTime,
    cutOffDate,
    propertyOverrides,
    configId,
    activeReminders,
    excludedEmailIds,
  ]);

  const addPropertyOverride = () => {
    setPropertyOverrides((prev) => [...prev, { name: "", value: "" }]);
  };

  const removePropertyOverride = (index: number) => {
    setPropertyOverrides((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePropertyOverride = (
    index: number,
    field: "name" | "value",
    val: string,
  ) => {
    setPropertyOverrides((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: val } : p)),
    );
  };

  const selectedConfig = defaults?.availableConfigs.find((c) => c.id === configId) ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: "95vw", maxWidth: 1800, height: "90vh" } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Simulate Reminders & Actions</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", gap: 2, overflow: "hidden", p: 2 }}>
        {/* Left Panel - Controls */}
        <Box
          sx={{
            width: 380,
            minWidth: 380,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pr: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Shipment: {shipmentId}
          </Typography>

          {loadingDefaults ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Autocomplete
                options={defaults?.availableConfigs ?? []}
                getOptionLabel={(option) => option.name}
                value={selectedConfig}
                onChange={(_, val) => setConfigId(val?.id ?? "")}
                size="small"
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Pipeline Config"
                    helperText="EmailAutomationPipelineConfig"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />

              <TextField
                label="Cut off events after date"
                type="datetime-local"
                value={cutOffDate}
                onChange={(e) => setCutOffDate(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText={cutOffDate
                  ? `Timeline filtered to ${timelineEvents.length}/${allEvents.length} events`
                  : "Emails after this date are excluded from timeline & simulation"
                }
              />

              {/* Email Selection Controls */}
              {allEmailIds.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary">
                    {selectedEmailIds.size} of {allEmailIds.length} emails selected
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setExcludedEmailIds(new Set(allEmailIds))}
                    disabled={excludedEmailIds.size === allEmailIds.length}
                    sx={{ minWidth: "auto", textTransform: "none", fontSize: "11px" }}
                  >
                    Exclude All
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setExcludedEmailIds(new Set())}
                    disabled={excludedEmailIds.size === 0}
                    sx={{ minWidth: "auto", textTransform: "none", fontSize: "11px" }}
                  >
                    Include All
                  </Button>
                </Box>
              )}

              <TextField
                label="Run reminders at time"
                type="datetime-local"
                value={runRemindersAtTime}
                onChange={(e) => setRunRemindersAtTime(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Simulation 'now' time"
              />

              {/* Property Overrides */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Property Overrides
                </Typography>
                {propertyOverrides.map((override, idx) => (
                  <Stack key={idx} direction="row" spacing={1} sx={{ mb: 1 }} alignItems="flex-start">
                    <Autocomplete
                      options={ALL_REFERENCE_NAMES as unknown as string[]}
                      value={override.name || null}
                      onChange={(_, val) => updatePropertyOverride(idx, "name", val || "")}
                      size="small"
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField {...params} label="Property" />
                      )}
                    />
                    <TextField
                      label="Value"
                      value={override.value}
                      onChange={(e) => updatePropertyOverride(idx, "value", e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      helperText={
                        DATE_PROPERTY_NAMES.has(override.name as any)
                          ? "Format: YYYY-MM-DD"
                          : undefined
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={() => removePropertyOverride(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addPropertyOverride}
                >
                  Add Override
                </Button>
              </Box>

              {/* Active Reminders Multi-select */}
              {defaults && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Active Reminders</InputLabel>
                  <Select
                    multiple
                    value={activeReminders}
                    onChange={(e) =>
                      setActiveReminders(
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value,
                      )
                    }
                    input={<OutlinedInput label="Active Reminders" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((val) => (
                          <Chip key={val} label={val} size="small" />
                        ))}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  >
                    {defaults.allReminderTypes.map((rt) => (
                      <MenuItem key={rt.type} value={rt.type}>
                        <Checkbox checked={activeReminders.includes(rt.type)} size="small" />
                        <ListItemText primary={rt.name} secondary={rt.type} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Enabled Action Reminders Multi-select */}
              {defaults && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Enabled Action Reminders</InputLabel>
                  <Select
                    multiple
                    value={enabledActionReminders}
                    onChange={(e) =>
                      setEnabledActionReminders(
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value,
                      )
                    }
                    input={<OutlinedInput label="Enabled Action Reminders" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((val) => (
                          <Chip key={val} label={val} size="small" />
                        ))}
                      </Box>
                    )}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                  >
                    {defaults.allReminderTypes.map((rt) => (
                      <MenuItem key={rt.type} value={rt.type}>
                        <Checkbox checked={enabledActionReminders.includes(rt.type)} size="small" />
                        <ListItemText primary={rt.name} secondary={rt.type} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
                onClick={handleSimulate}
                disabled={loading}
                fullWidth
              >
                {loading ? "Simulating..." : "Simulate Reminders & Actions"}
              </Button>

              <Divider sx={{ my: 1 }} />

              {/* Re-run Pipeline Section */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Re-run Pipeline
                </Typography>
                <AdminWorkflowActionTracker
                  command="rerun-pipeline-on-shipment"
                  commandArgs={{ shipmentId }}
                  buttonLabel="Re-run Pipeline"
                  compact
                  confirmMessage="This is a real operation, not a simulation. All emails for this shipment will be re-processed through the lightweight pipeline."
                  onComplete={refetch}
                />
              </Box>
            </>
          )}
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Middle Panel - Shipment Details + Timeline */}
        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {eventsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Shipment Details - Collapsible */}
              {shipmentWithOverrides && (
                <Box sx={{ px: 2, flexShrink: 0 }}>
                  <Box
                    onClick={() => setDetailsExpanded(!detailsExpanded)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                      py: 1,
                      borderRadius: 1,
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <ExpandMoreIcon
                      sx={{
                        fontSize: "1.25rem",
                        color: "text.secondary",
                        transform: detailsExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Shipment Details
                    </Typography>
                    {overrideUpdates.length > 0 && (
                      <Chip
                        label={`${overrideUpdates.length} override${overrideUpdates.length > 1 ? "s" : ""}`}
                        size="small"
                        color="info"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>
                  <Collapse in={detailsExpanded}>
                    <Box sx={{ pt: 1, pb: 1 }}>
                      <ShipmentDetails
                        shipment={shipmentWithOverrides}
                        updates={overrideUpdates}
                        maxVisibleDetails={10}
                        hideTitle
                      />
                    </Box>
                  </Collapse>
                  <Divider sx={{ mt: 0 }} />
                </Box>
              )}

              {/* Timeline */}
              <ShipmentTimelineRenderer
                events={timelineEvents}
                showEventCategories={true}
                filtersCollapsible={false}
                inlineDates={true}
                onDownloadAttachment={downloadAttachment}
                onOpenEmail={(emailId) => dispatch(openEmailModal(emailId))}
                showCheckboxes={true}
                selectedEmailIds={selectedEmailIds}
                onToggleEmailSelection={handleToggleEmail}
                onStyleMessage={handleStyleMessage}
              />
            </>
          )}
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Right Panel - Simulation Results */}
        <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
          {error && (
            <Paper sx={{ p: 2, bgcolor: "error.light", color: "error.contrastText" }}>
              <Typography>{error}</Typography>
            </Paper>
          )}

          {!result && !loading && !error && (
            <Typography color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
              Configure simulation parameters and click "Simulate Reminders & Actions"
            </Typography>
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {result && (
            <>
              {/* Reminders */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    Reminders (New: {result.reminders.newReminders.length}, Resolved: {result.reminders.resolvedReminders.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Emails analyzed: {result.reminders.analysisStats.emailAnalyzedCount} |
                    Updates: {result.reminders.analysisStats.updateAnalyzedCount} |
                    Total events: {result.reminders.analysisStats.totalEventsCount}
                  </Typography>

                  {result.reminders.diagnosticEvents && result.reminders.diagnosticEvents.length > 0 && (
                    <Accordion sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="caption">
                          Logs ({result.reminders.diagnosticEvents.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <DiagnosticEventsDisplay
                          events={{ events: result.reminders.diagnosticEvents as any }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {result.reminders.newReminders.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>New Reminders</Typography>
                      {result.reminders.newReminders.map((r, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Chip label={r.type} size="small" color="primary" />
                            <Typography variant="body2" fontWeight="bold">{r.name}</Typography>
                          </Box>
                          {r.description && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {r.description}
                            </Typography>
                          )}
                          <Typography variant="caption" display="block">
                            Due: {new Date(r.dueTime).toLocaleString()} |
                            Trigger: {new Date(r.triggerTime).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.reason.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}

                  {result.reminders.resolvedReminders.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Resolved Reminders</Typography>
                      {result.reminders.resolvedReminders.map((r, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                          <Typography variant="body2">
                            Reminder ID: {r.reminderId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.reason.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}

                  {result.reminders.newReminders.length === 0 &&
                    result.reminders.resolvedReminders.length === 0 && (
                      <Typography color="text.secondary">No reminder changes detected</Typography>
                    )}
                </AccordionDetails>
              </Accordion>

              {/* Actions */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    Actions ({result.actions.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {result.actions.length === 0 ? (
                    <Typography color="text.secondary">No actions generated</Typography>
                  ) : (
                    result.actions.map((action, idx) => (
                      <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Chip label={action.actionType} size="small" color="secondary" />
                          <Typography variant="body2">
                            For: {action.forReminderName} ({action.forReminderType})
                          </Typography>
                        </Box>

                        {/* Draft email display */}
                        {action.actionType === "draftEmail" && action.data && (
                          <Box sx={{ ml: 2, mb: 1 }}>
                            <>
                            {action.resolvedReplyToSubject && (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <Typography variant="caption">
                                  Reply to: {action.resolvedReplyToSubject}
                                </Typography>
                                {action.resolvedReplyToEmailId && (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => dispatch(openEmailModal(action.resolvedReplyToEmailId!))}
                                    sx={{ minWidth: "auto", textTransform: "none" }}
                                  >
                                    Open Email
                                  </Button>
                                )}
                              </Box>
                            )}
                            {action.data.to && (
                              <Typography variant="caption" display="block">
                                To: {Array.isArray(action.data.to) ? (action.data.to as string[]).join(", ") : String(action.data.to)}
                              </Typography>
                            )}
                            {action.data.cc && (
                              <Typography variant="caption" display="block">
                                CC: {Array.isArray(action.data.cc) ? (action.data.cc as string[]).join(", ") : String(action.data.cc)}
                              </Typography>
                            )}
                            {action.data.subject && (
                              <Typography variant="caption" display="block">
                                Subject: {String(action.data.subject)}
                              </Typography>
                            )}
                            {action.data.messageContent && (
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  mt: 1,
                                  bgcolor: "grey.50",
                                  maxHeight: 200,
                                  overflowY: "auto",
                                  whiteSpace: "pre-wrap",
                                  fontSize: "12px",
                                }}
                              >
                                {String(action.data.messageContent)}
                              </Paper>
                            )}
                            {action.resolvedAttachments && action.resolvedAttachments.length > 0 && (
                              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                                <Typography variant="caption" fontWeight="bold">Attachments:</Typography>
                                {action.resolvedAttachments.map((att, attIdx) => (
                                  <Chip
                                    key={attIdx}
                                    icon={<AttachFileIcon />}
                                    label={att.filename}
                                    size="small"
                                    clickable
                                    onClick={() => downloadAttachment(att.emailId, att.attachmentId, att.filename)}
                                  />
                                ))}
                              </Box>
                            )}
                            </>
                          </Box>
                        )}

                        {/* Non-draftEmail: show raw data */}
                        {action.actionType !== "draftEmail" && action.actionType !== "error" && (
                          <Paper
                            variant="outlined"
                            sx={{ p: 1, mt: 1, maxHeight: 150, overflowY: "auto", fontSize: "11px", fontFamily: "monospace" }}
                          >
                            {JSON.stringify(action.data, null, 2)}
                          </Paper>
                        )}

                        {/* Error action */}
                        {action.actionType === "error" && (
                          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                            {String(action.data.error)}
                          </Typography>
                        )}

                        {/* GPT Requests */}
                        {action.gptRequestId && (
                          <Box sx={{ mt: 1 }}>
                            <GptRequestsAccordion
                              requests={[{ name: "generateActions", id: action.gptRequestId }]}
                            />
                          </Box>
                        )}

                        {/* Diagnostic Events */}
                        {action.diagnosticEvents && action.diagnosticEvents.length > 0 && (
                          <Accordion sx={{ mt: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="caption">
                                Logs ({action.diagnosticEvents.length})
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <DiagnosticEventsDisplay
                                events={{ events: action.diagnosticEvents as any }}
                              />
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Paper>
                    ))
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Store Results */}
              {result.reminders.newReminders.length > 0 && !persistResult && (
                <Box sx={{ mt: 1 }}>
                  {!showPersistConfirm ? (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<SaveIcon />}
                      onClick={() => setShowPersistConfirm(true)}
                      disabled={persisting || !configId}
                      fullWidth
                    >
                      Store Results to Database
                    </Button>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Confirm: Persist simulation results?
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        This will create {result.reminders.newReminders.length} reminder(s)
                        {result.actions.filter((a) => a.actionType !== "error").length > 0 &&
                          ` and ${result.actions.filter((a) => a.actionType !== "error").length} action(s)`}{" "}
                        in the database. This cannot be undone from this dialog.
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          color="warning"
                          size="small"
                          onClick={handlePersist}
                          disabled={persisting}
                          startIcon={persisting ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        >
                          {persisting ? "Persisting..." : "Confirm"}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowPersistConfirm(false)}
                          disabled={persisting}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Persist result feedback */}
              {persistResult && (
                <Alert
                  severity={
                    persistResult.createdReminderIds.length > 0
                      ? persistResult.errors.length > 0
                        ? "warning"
                        : "success"
                      : "error"
                  }
                  sx={{ mt: 1 }}
                >
                  {persistResult.createdReminderIds.length > 0 && (
                    <Typography variant="body2">
                      Created {persistResult.createdReminderIds.length} reminder(s)
                      {persistResult.createdActionIds.length > 0 &&
                        `, ${persistResult.createdActionIds.length} action(s)`}
                      . Pipeline result: {persistResult.pipelineResultId}
                    </Typography>
                  )}
                  {persistResult.errors.map((err, i) => (
                    <Typography key={i} variant="body2" color="error">
                      {err}
                    </Typography>
                  ))}
                </Alert>
              )}

            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function toLocalDateTimeString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}
